import {useMemo} from 'react';
import type {Concept, LinkAction} from 'types';
import type {SetSourceValue} from 'sceneExtensions/types';
import type {Linking} from 'schema/types';
import type {
  LinkedValueLink,
  VerifiedLinkedValueLink,
} from 'schemaDefinition/types';
import {
  getAgentPlace,
  getLinkTargetConcreteEntityType,
  isAgentValue,
} from 'services/utils/functions';
import type {EntityState, LinkedValueSearchFieldConfig} from '../types';
import {
  canCopySourceToEntity,
  canLinkEntityToSource,
  canMoveSourceToEntity,
} from '../functions';
import {getEntityValueId} from '../functions/getEntityValueId';

export const usePreviewLinkingConfiguration = (
  previewEntityType: Concept,
  entityState: EntityState | undefined,
  currentSourceValue: LinkedValueLink | undefined,
  currentSourceConfig: LinkedValueSearchFieldConfig | undefined,
  setSourceValue:
    | SetSourceValue<VerifiedLinkedValueLink, Exclude<LinkAction, 'changeLink'>>
    | undefined,
) => {
  // Config preview according to current linked value
  return useMemo((): Linking | undefined => {
    const sourceValue: VerifiedLinkedValueLink | undefined =
      currentSourceValue?.entityId
        ? (currentSourceValue as VerifiedLinkedValueLink)
        : undefined;
    const value = entityState?.data;
    const entityValueId = value ? getEntityValueId(value) : undefined;
    if (!entityValueId || !setSourceValue) {
      return undefined;
    }
    return canLinkEntityToSource(previewEntityType, currentSourceConfig)
      ? {
          entityId: entityValueId,
          entityType: getLinkTargetConcreteEntityType(value),
          nationalId: isAgentValue(value) ? value.nationalId : undefined,
          place: getAgentPlace(value),
          currentValue: sourceValue,
          // Only takes on argument passed to setSourceValue
          link: (...args) => setSourceValue(args[0], 'link', 'close'),
          unlink: () => setSourceValue(undefined, 'unlink'),
        }
      : canMoveSourceToEntity(previewEntityType, currentSourceConfig)
        ? {
            entityId: entityValueId,
            entityType: getLinkTargetConcreteEntityType(value),
            nationalId: isAgentValue(value) ? value.nationalId : undefined,
            place: getAgentPlace(value),
            currentValue: sourceValue,
            move: (...args) => setSourceValue(args[0], 'move', 'keepOpen'),
            copy: (...args) => setSourceValue(args[0], 'copy', 'close'),
          }
        : canCopySourceToEntity(previewEntityType, currentSourceConfig)
          ? {
              entityId: entityValueId,
              entityType: getLinkTargetConcreteEntityType(value),
              nationalId: isAgentValue(value) ? value.nationalId : undefined,
              place: getAgentPlace(value),
              currentValue: sourceValue,
              copy: (...args) => setSourceValue(args[0], 'copy', 'close'),
            }
          : undefined;
  }, [
    currentSourceConfig,
    currentSourceValue,
    entityState?.data,
    previewEntityType,
    setSourceValue,
  ]);
};
